import * as React from 'react'
import { AlertCircle as WarningIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  collectAdditionalProperties,
  useGetPlantRobotNodeDetailQuery,
  usePlantSessionPaint,
  type PlantEquipment,
  type PlantPLCTag,
  type PlantRobotArm,
  type PlantRobotArmDetail,
  type PlantRobotCondition,
  type PlantRobotConditionDetail,
  type PlantRobotController,
  type PlantRobotControllerDetail,
  type PlantRobotIOSignal,
  type PlantRobotJob,
  type PlantRobotJobDetail,
  type PlantRobotNodeKind,
  type PlantRobotPlcTagMapping,
  type PlantRobotRelationGroup,
  type PlantRobotRelationSite,
  type PlantRobotSignalDetail,
  type PlantRobotSupervisionRef,
  type PlantPlcScadaTag,
  type PlantVisionData,
  type PlantVisionDataDetail,
  type PlantVisionDataRef,
  type PlantVisionProcess,
  type PlantVisionProcessDetail,
  type PlantVisionProcessLink,
  type PlantVisionSignalLink
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { plantHref } from '../../lib/plant-links'
import { CollapsibleContent } from './CollapsibleContent'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { robotIconFor, robotLabelFor } from './robot-icon'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionTitleMark,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

/** Long enumerations start collapsed so controller/arm pages stay scannable. */
const COLLAPSE_THRESHOLD = 10

export function RoboticsDetailPanel({ nodeId }: { nodeId: string }): React.ReactElement {
  const detail = useGetPlantRobotNodeDetailQuery(nodeId, { skip: !nodeId })

  if (detail.isError) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <Empty
          title="Couldn't load this Robotics node"
          hint="The node may have been deleted, or the proxy is unreachable."
          icon={WarningIcon}
        />
      </div>
    )
  }
  if (detail.isLoading || !detail.data) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <div className="text-sm text-ink-3">Loading Robotics node…</div>
      </div>
    )
  }

  switch (detail.data.kind) {
    case 'RobotController':
      return <ControllerDetail detail={detail.data} />
    case 'RobotArm':
      return <ArmDetail detail={detail.data} />
    case 'RobotJob':
      return <JobDetail detail={detail.data} />
    case 'RobotIOSignal':
      return <SignalDetail detail={detail.data} />
    case 'RobotCondition':
      return <ConditionDetail detail={detail.data} />
    case 'VisionProcess':
      return <VisionProcessDetail detail={detail.data} />
    case 'VisionData':
      return <VisionDataDetail detail={detail.data} />
  }
  throw new Error(`Unsupported Robotics node kind ${(detail.data as { kind: string }).kind}`)
}

function ControllerDetail({ detail }: { detail: PlantRobotControllerDetail }): React.ReactElement {
  const {
    controller,
    arms,
    jobs,
    signals,
    conditions,
    controlledEquipment,
    visionProcesses,
    visionData,
    supervises,
    supervisedBy
  } = detail
  const armGhosts = useVisibleGhosts(controller.id, 'RobotArm')
  const jobGhosts = useVisibleGhosts(controller.id, 'RobotJob')
  const signalGhosts = useVisibleGhosts(controller.id, 'RobotIOSignal')
  const conditionGhosts = useVisibleGhosts(controller.id, 'RobotCondition')
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties(
        'RobotController',
        controller as unknown as Record<string, unknown>
      ),
    [controller]
  )
  return (
    <>
      <DetailHeader
        kind="RobotController"
        entityId={controller.id}
        isStray={detail.isStray}
        name={controller.display_name ?? controller.name}
        subtitle={controllerSubtitle(controller)}
        needsReview={controller.needs_review}
      />
      <InferenceBadges node={controller} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Arms" value={arms.length} />
        <Stat label="Jobs" value={jobs.length} />
        <Stat label="Signals" value={signals.length} />
        <Stat label="Conditions" value={conditions.length} />
      </div>
      <TopologySection supervises={supervises} supervisedBy={supervisedBy} />
      {controlledEquipment.length > 0 && <EquipmentLinks equipment={controlledEquipment} />}
      {(arms.length > 0 || armGhosts.length > 0) && (
        <NodeTileGrid label="Arms">
          {arms.map((arm) => (
            <NodeTile
              key={arm.id}
              entityId={arm.id}
              href={plantHref(arm.id, 'RobotArm', arm)}
              kind="RobotArm"
              title={arm.name}
              subtitle={armSubtitle(arm)}
              needsReview={arm.needs_review}
            />
          ))}
          {armGhosts.map((ghost) => (
            <RobotGhostTile key={ghost.id} kind="RobotArm" ghost={ghost} />
          ))}
        </NodeTileGrid>
      )}
      {jobGhosts.length > 0 && (
        <NodeTileGrid label="Jobs">
          {jobGhosts.map((ghost) => (
            <RobotGhostTile key={ghost.id} kind="RobotJob" ghost={ghost} />
          ))}
        </NodeTileGrid>
      )}
      {(signals.length > 0 || signalGhosts.length > 0) && (
        <SignalTable label="I/O signals" signals={signals} ghosts={signalGhosts} />
      )}
      {(conditions.length > 0 || conditionGhosts.length > 0) && (
        <ConditionTable conditions={conditions} ghosts={conditionGhosts} />
      )}
      {visionProcesses.length > 0 && (
        <NodeTileGrid label="Vision processes">
          {visionProcesses.map((process) => (
            <NodeTile
              key={process.id}
              entityId={process.id}
              href={plantHref(process.id, 'VisionProcess', process)}
              kind="VisionProcess"
              title={process.name}
              subtitle={visionProcessSubtitle(process)}
              needsReview={process.needs_review}
            />
          ))}
        </NodeTileGrid>
      )}
      {visionData.length > 0 && <VisionDataTable data={visionData} />}
      <EntityDetailsInspector entity={controller} additionalProperties={additionalProperties} />
    </>
  )
}

function ArmDetail({ detail }: { detail: PlantRobotArmDetail }): React.ReactElement {
  const { arm, controller, jobs, signals, conditions } = detail
  const jobGhosts = useVisibleGhosts(arm.id, 'RobotJob')
  const signalGhosts = useVisibleGhosts(arm.id, 'RobotIOSignal')
  const conditionGhosts = useVisibleGhosts(arm.id, 'RobotCondition')
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('RobotArm', arm as unknown as Record<string, unknown>),
    [arm]
  )
  return (
    <>
      <DetailHeader
        kind="RobotArm"
        entityId={arm.id}
        isStray={detail.isStray}
        name={arm.name}
        parent={controller ? { kind: 'RobotController', node: controller } : null}
        subtitle={armSubtitle(arm)}
        needsReview={arm.needs_review}
      />
      <InferenceBadges node={arm} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Jobs" value={jobs.length} />
        <Stat label="Signals" value={signals.length} />
        <Stat label="Conditions" value={conditions.length} />
        <Stat label="Axes" value={arm.axis_count ?? '—'} />
      </div>
      {(jobs.length > 0 || jobGhosts.length > 0) && <JobRowTable jobs={jobs} ghosts={jobGhosts} />}
      {(signals.length > 0 || signalGhosts.length > 0) && (
        <SignalTable label="I/O signals" signals={signals} ghosts={signalGhosts} />
      )}
      {(conditions.length > 0 || conditionGhosts.length > 0) && (
        <ConditionTable conditions={conditions} ghosts={conditionGhosts} />
      )}
      <EntityDetailsInspector entity={arm} additionalProperties={additionalProperties} />
    </>
  )
}

function JobDetail({ detail }: { detail: PlantRobotJobDetail }): React.ReactElement {
  const {
    job,
    arm,
    controller,
    calls,
    calledBy,
    readSignals,
    writtenSignals,
    conditions,
    visionProcesses,
    visionData
  } = detail
  const readCount = React.useMemo(() => groupRelationSites(readSignals).length, [readSignals])
  const writeCount = React.useMemo(
    () => groupRelationSites(writtenSignals).length,
    [writtenSignals]
  )
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('RobotJob', job as unknown as Record<string, unknown>, [
        'source_code'
      ]),
    [job]
  )
  return (
    <>
      <DetailHeader
        kind="RobotJob"
        entityId={job.id}
        isStray={detail.isStray}
        name={job.name}
        parent={arm ? { kind: 'RobotArm', node: arm } : null}
        secondaryParent={controller ? { kind: 'RobotController', node: controller } : null}
        subtitle={jobSubtitle(job)}
        needsReview={job.needs_review}
      />
      <InferenceBadges node={job} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Instructions" value={job.instruction_count ?? '—'} />
        <Stat label="Positions" value={job.position_count ?? '—'} />
        <Stat label="Reads" value={readCount} />
        <Stat label="Writes" value={writeCount} />
      </div>
      {job.comment && <p className="m-0 max-w-3xl text-sm text-ink-3">{job.comment}</p>}
      {(calls.length > 0 || calledBy.length > 0) && (
        <JobSiteLinksSection calls={calls} calledBy={calledBy} />
      )}
      {readSignals.length > 0 && (
        <SignalSiteTable label="Signals read by this job" sites={readSignals} />
      )}
      {writtenSignals.length > 0 && (
        <SignalSiteTable label="Signals written by this job" sites={writtenSignals} />
      )}
      {conditions.length > 0 && <ConditionSiteTable sites={conditions} />}
      {(visionProcesses.length > 0 || visionData.length > 0) && (
        <VisionSiteTable processes={visionProcesses} data={visionData} />
      )}
      {job.source_code && <SourceCodeSection code={job.source_code} />}
      <EntityDetailsInspector entity={job} additionalProperties={additionalProperties} />
    </>
  )
}

function SignalDetail({ detail }: { detail: PlantRobotSignalDetail }): React.ReactElement {
  const {
    signal,
    controller,
    readBy,
    writtenBy,
    scadaTags,
    plcMappings,
    visionOutputs,
    visionTriggers
  } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('RobotIOSignal', signal as unknown as Record<string, unknown>),
    [signal]
  )
  return (
    <>
      <DetailHeader
        kind="RobotIOSignal"
        entityId={signal.id}
        isStray={detail.isStray}
        name={signal.name}
        parent={controller ? { kind: 'RobotController', node: controller } : null}
        subtitle={signalSubtitle(signal)}
        needsReview={signal.needs_review}
      />
      <InferenceBadges node={signal} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Direction" value={signal.direction ?? '—'} />
        <Stat label="Signal" value={signal.signal_number ?? '—'} />
        <Stat label="Read by" value={readBy.length} />
        <Stat label="Written by" value={writtenBy.length} />
      </div>
      {signal.comment && <p className="m-0 max-w-3xl text-sm text-ink-3">{signal.comment}</p>}
      {scadaTags.length > 0 && <RobotScadaMappingsSection tags={scadaTags} />}
      {plcMappings.length > 0 && <RobotPlcMappingsSection mappings={plcMappings} />}
      {visionOutputs.length > 0 && (
        <VisionProcessChips label="Carries vision results from" links={visionOutputs} />
      )}
      {visionTriggers.length > 0 && (
        <VisionProcessChips label="Triggers vision" links={visionTriggers} />
      )}
      {(readBy.length > 0 || writtenBy.length > 0) && (
        <JobSiteLinksSection
          calls={readBy}
          calledBy={writtenBy}
          callsLabel="Read by"
          calledByLabel="Written by"
        />
      )}
      <EntityDetailsInspector entity={signal} additionalProperties={additionalProperties} />
    </>
  )
}

function ConditionDetail({ detail }: { detail: PlantRobotConditionDetail }): React.ReactElement {
  const { condition, controller, usedBy } = detail
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties(
        'RobotCondition',
        condition as unknown as Record<string, unknown>
      ),
    [condition]
  )
  return (
    <>
      <DetailHeader
        kind="RobotCondition"
        entityId={condition.id}
        isStray={detail.isStray}
        name={condition.name}
        parent={controller ? { kind: 'RobotController', node: controller } : null}
        subtitle={conditionSubtitle(condition)}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Scheme" value={condition.scheme ?? '—'} />
        <Stat label="Index" value={condition.index ?? '—'} />
        <Stat label="Used by" value={usedBy.length} />
      </div>
      {condition.comment && <p className="m-0 max-w-3xl text-sm text-ink-3">{condition.comment}</p>}
      {usedBy.length > 0 && (
        <JobSiteLinksSection calls={usedBy} calledBy={[]} callsLabel="Used by" />
      )}
      <EntityDetailsInspector entity={condition} additionalProperties={additionalProperties} />
    </>
  )
}

function VisionProcessDetail({ detail }: { detail: PlantVisionProcessDetail }): React.ReactElement {
  const { process, controller, usedBy, outputs, triggers, visionData } = detail
  const usedByCount = React.useMemo(() => groupRelationSites(usedBy).length, [usedBy])
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('VisionProcess', process as unknown as Record<string, unknown>),
    [process]
  )
  return (
    <>
      <DetailHeader
        kind="VisionProcess"
        entityId={process.id}
        isStray={detail.isStray}
        name={process.name}
        parent={controller ? { kind: 'RobotController', node: controller } : null}
        subtitle={visionProcessSubtitle(process)}
        needsReview={process.needs_review}
      />
      <VisionBadges node={process} />
      {process.comment && <p className="m-0 max-w-3xl text-sm text-ink-3">{process.comment}</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Used by jobs" value={usedByCount} />
        <Stat label="Result signals" value={outputs.length} />
        <Stat label="Trigger signals" value={triggers.length} />
        <Stat label="Vision data" value={visionData.length} />
      </div>
      {outputs.length > 0 && (
        <VisionSignalLinkTable
          label="Results reported to I/O"
          subtitle="Vision results the jobs write to robot outputs, outputs the vision data file drives, and the PLC / SCADA tags those outputs map to"
          links={outputs}
          showResult
        />
      )}
      {triggers.length > 0 && (
        <VisionSignalLinkTable
          label="Triggered by"
          subtitle="Inputs the jobs wait on right before running this vision process, or that its vision data file names"
          links={triggers}
        />
      )}
      {visionData.length > 0 && (
        <VisionDataRefChips label="Uses vision data" refs={visionData} kind="VisionData" />
      )}
      {usedBy.length > 0 && (
        <JobSiteLinksSection calls={usedBy} calledBy={[]} callsLabel="Used by jobs" />
      )}
      <EntityDetailsInspector entity={process} additionalProperties={additionalProperties} />
    </>
  )
}

function VisionDataDetail({ detail }: { detail: PlantVisionDataDetail }): React.ReactElement {
  const { data, controller, usedBy, processes } = detail
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('VisionData', data as unknown as Record<string, unknown>),
    [data]
  )
  return (
    <>
      <DetailHeader
        kind="VisionData"
        entityId={data.id}
        isStray={detail.isStray}
        name={data.name}
        parent={controller ? { kind: 'RobotController', node: controller } : null}
        subtitle={visionDataSubtitle(data)}
        needsReview={data.needs_review}
      />
      {data.comment && <p className="m-0 max-w-3xl text-sm text-ink-3">{data.comment}</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Type" value={humanize(data.vision_data_type) ?? '—'} />
        <Stat label="Used by jobs" value={groupRelationSites(usedBy).length} />
        <Stat label="Referenced by" value={processes.length} />
      </div>
      {processes.length > 0 && (
        <VisionDataRefChips
          label="Referenced by vision processes"
          refs={processes}
          kind="VisionProcess"
        />
      )}
      {usedBy.length > 0 && (
        <JobSiteLinksSection calls={usedBy} calledBy={[]} callsLabel="Used by jobs" />
      )}
      <EntityDetailsInspector entity={data} additionalProperties={additionalProperties} />
    </>
  )
}

function VisionBadges({ node }: { node: PlantVisionProcess }): React.ReactElement | null {
  const entries = [
    ...(node.applications ?? []).map((a) => `application: ${humanize(a)}`),
    ...(node.result_kinds ?? []).map((r) => `result: ${humanize(r)}`),
    node.offset_applied ? 'offset applied to motion' : null,
    node.vision_registers?.length ? `VR[${node.vision_registers.join(', ')}]` : null,
    node.remote_controller ? `remote: ${node.remote_controller}` : null,
    ...(node.tools ?? []).map((t) => `tool: ${t}`)
  ].filter((entry): entry is string => Boolean(entry))
  if (entries.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map((entry) => (
        <span key={entry} className="rounded-full border bg-bg-sunk px-2 py-0.5 text-11 text-ink-3">
          {entry}
        </span>
      ))}
    </div>
  )
}

function VisionSignalLinkTable({
  label,
  subtitle,
  links,
  showResult = false
}: {
  label: string
  subtitle: string
  links: PlantVisionSignalLink[]
  showResult?: boolean
}): React.ReactElement {
  return (
    <CollapsibleRowSection label={label} count={links.length}>
      <p className="m-0 mb-2 text-xs text-ink-3">{subtitle}</p>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Signal</th>
              <th className="whitespace-nowrap px-3 py-1.5">Address</th>
              {showResult && <th className="px-3 py-1.5">Result</th>}
              <th className="px-3 py-1.5">Jobs</th>
              <th className="px-3 py-1.5">PLC tag</th>
              <th className="px-3 py-1.5">SCADA tag</th>
            </tr>
          </thead>
          <tbody>
            {links.map((link) => (
              <tr key={link.signal.id} className="border-t">
                <td className="max-w-[14rem] truncate px-3 py-1.5">
                  <Link
                    to={plantHref(link.signal.id, 'RobotIOSignal', link.signal)}
                    className="truncate font-medium text-brand no-underline hover:underline"
                    title={link.signal.name}
                  >
                    {link.signal.name}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 font-mono text-10 text-ink-3">
                  {link.signal.address ?? '—'}
                </td>
                {showResult && (
                  <td className="px-3 py-1.5 text-ink-2">
                    {link.resultKinds.map(humanize).join(', ') ||
                      (link.via.includes('vision_data') ? 'set in vision data' : '—')}
                    {link.via.includes('conditional') && (
                      <span className="ml-1 text-10 text-ink-4">(conditional)</span>
                    )}
                  </td>
                )}
                <td className="px-3 py-1.5 font-mono text-10 text-ink-3">
                  {link.jobs.join(', ')}
                  {link.via.includes('vision_data') && (
                    <span
                      className={link.jobs.length ? 'ml-1 font-sans' : 'font-sans'}
                      title={link.dataFields.join(', ')}
                    >
                      {link.jobs.length ? '+ vision data' : 'vision data'}
                    </span>
                  )}
                  {link.jobs.length === 0 && !link.via.includes('vision_data') && '—'}
                </td>
                <td className="px-3 py-1.5">
                  <PlcTagCell plcTags={link.plcTags} />
                </td>
                <td className="px-3 py-1.5">
                  <ScadaTagCell tags={link.scadaTags} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function ScadaTagCell({ tags }: { tags: PlantPlcScadaTag[] }): React.ReactElement {
  if (tags.length === 0) return <span className="font-mono text-12">—</span>
  const [first, ...rest] = tags
  return (
    <span className="inline-flex max-w-[18rem] items-center gap-1 whitespace-nowrap font-mono text-12">
      <Link
        to={plantHref(first!.id, 'Tag', first!)}
        className="min-w-0 truncate text-brand no-underline hover:underline"
        title={tags.map((t) => t.tag_path ?? t.name).join(', ')}
      >
        {first!.tag_path ?? first!.name}
      </Link>
      {rest.length > 0 && <span className="shrink-0 text-ink-3">+{rest.length}</span>}
    </span>
  )
}

function VisionDataRefChips<T extends PlantVisionData | PlantVisionProcess>({
  label,
  refs,
  kind
}: {
  label: string
  refs: PlantVisionDataRef<T>[]
  kind: 'VisionProcess' | 'VisionData'
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label={label} subtitle={`${refs.length}`} />
      <div className="flex flex-wrap gap-2">
        {refs.map((ref) => (
          <Link
            key={ref.node.id}
            to={plantHref(ref.node.id, kind, ref.node)}
            className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/40 bg-sky-500/10 px-2.5 py-1 text-12 font-medium text-sky-800 no-underline hover:bg-sky-500/20 dark:text-sky-200"
            title={[
              ref.method ? `method: ${ref.method}` : null,
              ref.confidence !== undefined
                ? `confidence: ${Math.round(ref.confidence * 100)}%`
                : null
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <span>{ref.node.name}</span>
            {'vision_data_type' in ref.node && ref.node.vision_data_type && (
              <span className="font-mono text-10 opacity-80">
                {humanize(ref.node.vision_data_type)}
              </span>
            )}
            {ref.needsReview && (
              <span className="rounded border border-amber-500/50 bg-amber-500/10 px-1 py-px text-9 font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                Review
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  )
}

function VisionProcessChips({
  label,
  links
}: {
  label: string
  links: PlantVisionProcessLink[]
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label={label} subtitle={`${links.length}`} />
      <div className="flex flex-wrap gap-2">
        {links.map((link) => (
          <Link
            key={link.process.id}
            to={plantHref(link.process.id, 'VisionProcess', link.process)}
            className="inline-flex items-center gap-1.5 rounded-full border border-sky-400/40 bg-sky-500/10 px-2.5 py-1 text-12 font-medium text-sky-800 no-underline hover:bg-sky-500/20 dark:text-sky-200"
            title={link.jobs.length ? `jobs: ${link.jobs.join(', ')}` : undefined}
          >
            <span>{link.process.name}</span>
            {(link.resultKinds.length > 0 || link.via.includes('vision_data')) && (
              <span className="font-mono text-10 opacity-80">
                {link.resultKinds.map(humanize).join(', ') || 'set in vision data'}
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  )
}

function VisionDataTable({ data }: { data: PlantVisionData[] }): React.ReactElement {
  return (
    <CollapsibleRowSection label="Vision data" count={data.length}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Vision data</th>
              <th className="px-3 py-1.5">Details</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-t">
                <td className="px-3 py-1.5">
                  <span className="flex items-center gap-1.5">
                    <Link
                      to={plantHref(item.id, 'VisionData', item)}
                      className="font-medium text-ink no-underline hover:text-brand hover:underline"
                    >
                      {item.name}
                    </Link>
                    {item.needs_review && <ReviewBadge />}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-ink-3">{visionDataSubtitle(item) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function VisionSiteTable({
  processes,
  data
}: {
  processes: PlantRobotRelationSite<PlantVisionProcess>[]
  data: PlantRobotRelationSite<PlantVisionData>[]
}): React.ReactElement {
  const rows = React.useMemo(
    () => [
      ...groupRelationSites(processes).map((group) => ({
        kind: 'VisionProcess' as const,
        group
      })),
      ...groupRelationSites(data).map((group) => ({
        kind: 'VisionData' as const,
        group
      }))
    ],
    [processes, data]
  )
  return (
    <CollapsibleRowSection label="Vision" count={rows.length}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Vision data</th>
              <th className="px-3 py-1.5">Kind</th>
              <th className="px-3 py-1.5">Sites</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ kind, group }) => (
              <tr key={group.node.id} className="border-t">
                <td className="px-3 py-1.5">
                  <Link
                    to={plantHref(group.node.id, kind, group.node)}
                    className="font-medium text-ink no-underline hover:text-brand hover:underline"
                  >
                    {group.node.name}
                  </Link>
                </td>
                <td className="px-3 py-1.5 text-ink-3">
                  {kind === 'VisionProcess'
                    ? 'vision process'
                    : (humanize((group.node as PlantVisionData).vision_data_type) ?? 'vision data')}
                </td>
                <td className="px-3 py-1.5 font-mono text-10 text-ink-3">
                  {formatGroupedSites(group.sites) ?? '—'}
                  {group.sites.length > 1 && (
                    <span className="ml-1 text-ink-4">({group.sites.length})</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function DetailHeader({
  kind,
  entityId,
  isStray,
  name,
  parent,
  secondaryParent,
  subtitle,
  needsReview
}: {
  kind: PlantRobotNodeKind
  entityId: string
  isStray: boolean
  name: string
  parent?: {
    kind: PlantRobotNodeKind
    node: { id: string; name: string }
  } | null
  secondaryParent?: {
    kind: PlantRobotNodeKind
    node: { id: string; name: string }
  } | null
  subtitle?: string
  needsReview?: boolean
}): React.ReactElement {
  const Icon = robotIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <header className={sessionDetailHeaderClass('flex flex-col gap-2', mark)}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
        {parent ? <ParentLink parent={parent} /> : <span>{robotLabelFor(kind)}</span>}
        {secondaryParent && (
          <>
            <span aria-hidden>·</span>
            <ParentLink parent={secondaryParent} />
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 truncate text-2xl font-semibold text-ink">{name}</h1>
            <SessionTitleMark mark={mark} />
            {needsReview && <ReviewBadge />}
            <PlantStrayPill isStray={isStray} />
          </div>
          {subtitle && <p className="m-0 mt-0.5 text-sm text-ink-3">{subtitle}</p>}
        </div>
      </div>
    </header>
  )
}

function ParentLink({
  parent
}: {
  parent: { kind: PlantRobotNodeKind; node: { id: string; name: string } }
}): React.ReactElement {
  return (
    <Link
      to={plantHref(parent.node.id, parent.kind, parent.node)}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-ink-2 no-underline hover:bg-accent hover:text-accent-foreground"
    >
      <span aria-hidden>↑</span>
      <span>{parent.node.name}</span>
    </Link>
  )
}

function ReviewBadge(): React.ReactElement {
  return (
    <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300">
      needs review
    </Badge>
  )
}

function InferenceBadges({ node }: { node: object }): React.ReactElement | null {
  const raw = node as Record<string, unknown>
  const entries = [
    ['metadata', raw['metadata_method']],
    ['assignment', raw['assignment_method']],
    ['master job', raw['master_job_method']],
    ['mapping', raw['mapping_method']]
  ].filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0
  )
  if (entries.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([label, value]) => (
        <span key={label} className="rounded-full border bg-bg-sunk px-2 py-0.5 text-11 text-ink-3">
          {label}: {value}
        </span>
      ))}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
      <div className="text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
      <div className="mt-1 text-lg font-semibold text-ink">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  )
}

function NodeTileGrid({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label={label} />
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
    </section>
  )
}

function NodeTile({
  entityId,
  href,
  kind,
  title,
  subtitle,
  needsReview
}: {
  entityId: string
  href: string
  kind: PlantRobotNodeKind
  title: string
  subtitle?: string
  needsReview?: boolean
}): React.ReactElement {
  const Icon = robotIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <Link
      to={href}
      title={subtitle ? `${title}\n${subtitle}` : title}
      className={sessionMarkClass(
        'flex items-start gap-2 rounded-xl border bg-card p-3 text-card-foreground no-underline shadow-sm hover:border-brand/60',
        mark
      )}
    >
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
        <Icon size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 text-sm font-medium text-ink [overflow-wrap:anywhere]">
          {title}
        </span>
        {subtitle && <span className="block truncate text-xs text-ink-3">{subtitle}</span>}
        {((mark && mark !== 'dimmed') || needsReview) && (
          <span className="mt-1 flex flex-wrap items-center gap-1.5">
            <SessionMarkAffordances mark={mark} />
            {needsReview && <ReviewBadge />}
          </span>
        )}
      </span>
    </Link>
  )
}

function RobotGhostTile({
  kind,
  ghost
}: {
  kind: PlantRobotNodeKind
  ghost: PlantSessionGhostView
}): React.ReactElement {
  const Icon = robotIconFor(kind)
  return (
    <SessionGhostTile
      name={ghost.name ?? ghost.id}
      view={ghost.view}
      icon={
        <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
          <Icon size={15} />
        </span>
      }
    />
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className="m-0 text-sm font-semibold text-ink">{label}</h2>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function CollapsibleRowSection({
  label,
  count,
  children
}: {
  label: string
  count: number
  children: React.ReactNode
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(count <= COLLAPSE_THRESHOLD)
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-sm font-semibold text-ink">{label}</h2>
        <span className="text-xs text-ink-3">{count}</span>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="ml-auto m-0 border-0 bg-transparent p-0 text-12 font-medium text-brand hover:underline"
        >
          {expanded ? 'Collapse' : 'Expand'}
        </button>
      </div>
      <CollapsibleContent open={expanded}>{children}</CollapsibleContent>
    </section>
  )
}

function groupRelationSites<T extends { id: string; name?: string }>(
  sites: PlantRobotRelationSite<T>[]
): PlantRobotRelationGroup<T>[] {
  const byId = new Map<string, PlantRobotRelationGroup<T>>()
  const order: string[] = []
  const seenSites = new Map<string, Set<string>>()
  for (const site of sites) {
    let group = byId.get(site.node.id)
    if (!group) {
      group = { node: site.node, sites: [] }
      byId.set(site.node.id, group)
      order.push(site.node.id)
      seenSites.set(site.node.id, new Set())
    }
    const entry: { instruction?: string; line?: number; opcode?: string } = {}
    if (site.instruction) entry.instruction = site.instruction
    if (typeof site.line === 'number') entry.line = site.line
    if (site.opcode) entry.opcode = site.opcode
    if (!(entry.instruction || entry.line !== undefined || entry.opcode)) continue
    const siteKey = `${entry.line ?? ''}\0${entry.opcode ?? ''}\0${entry.instruction ?? ''}`
    const seen = seenSites.get(site.node.id)!
    if (seen.has(siteKey)) continue
    seen.add(siteKey)
    group.sites.push(entry)
  }
  return order.map((id) => byId.get(id)!)
}

function formatGroupedSites(
  sites: Array<{ instruction?: string; line?: number; opcode?: string }>
): string | null {
  if (sites.length === 0) return null
  if (sites.length === 1) return formatSiteSubtitle(sites[0]!)
  const lines = sites.map((s) => s.line).filter((line): line is number => typeof line === 'number')
  if (lines.length === sites.length) {
    const opcode = sites[0]?.opcode ?? sites[0]?.instruction
    return `lines ${lines.join(', ')}${opcode ? ` · ${opcode}` : ''}`
  }
  return sites.map((s) => formatSiteSubtitle(s) ?? 'site').join('; ')
}

function PlcTagCell({
  plcTags,
  plcTag
}: {
  plcTags?: PlantPLCTag[]
  plcTag?: string
}): React.ReactElement {
  if (plcTags && plcTags.length > 0) {
    const [first, ...rest] = plcTags
    return (
      <span className="inline-flex max-w-[16rem] items-center gap-1 whitespace-nowrap font-mono text-12">
        <Link
          to={plantHref(first!.id, 'PLCTag', first!)}
          className="min-w-0 truncate text-brand no-underline hover:underline"
          title={plcTags.map((t) => t.name).join(', ')}
        >
          {first!.name}
        </Link>
        {rest.length > 0 && <span className="shrink-0 text-ink-3">+{rest.length}</span>}
      </span>
    )
  }
  return <span className="whitespace-nowrap font-mono text-12">{plcTag ?? '—'}</span>
}

function EquipmentLinks({ equipment }: { equipment: PlantEquipment[] }): React.ReactElement {
  return (
    <section>
      <SectionHeader label="Controlled robotic cells" subtitle={`${equipment.length}`} />
      <div className="flex flex-wrap gap-2">
        {equipment.map((eq) => (
          <Link
            key={eq.id}
            to={plantHref(eq.id, 'Equipment', eq)}
            className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2.5 py-1 text-12 font-medium text-violet-700 no-underline hover:bg-violet-500/20 dark:text-violet-300"
          >
            {eq.name}
          </Link>
        ))}
      </div>
    </section>
  )
}

function JobRowTable({
  jobs,
  ghosts = []
}: {
  jobs: PlantRobotJob[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const total = jobs.length + ghosts.length
  return (
    <CollapsibleRowSection label="Jobs" count={total}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Job</th>
              <th className="px-3 py-1.5">Details</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => {
              const mark = markFor(job.id)
              return (
                <tr key={job.id} className={sessionMarkClass('border-t', mark)}>
                  <td className="px-3 py-1.5">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Link
                        to={plantHref(job.id, 'RobotJob', job)}
                        className="min-w-0 flex-1 font-medium text-ink no-underline hover:text-brand hover:underline"
                      >
                        {job.name}
                      </Link>
                      <SessionMarkAffordances mark={mark} />
                      {job.needs_review && <ReviewBadge />}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-ink-3">{jobSubtitle(job) ?? '—'}</td>
                </tr>
              )
            })}
            {ghosts.map((ghost) => (
              <tr
                key={ghost.id}
                className={
                  ghost.view === 'dimmed'
                    ? 'border-t border-line bg-bg-sunk/70'
                    : 'border-t border-st-down/30 bg-st-down-bg'
                }
              >
                <td className="px-3 py-1.5" colSpan={2}>
                  <RobotGhostTile kind="RobotJob" ghost={ghost} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function SignalTable({
  label,
  signals,
  ghosts = []
}: {
  label: string
  signals: PlantRobotIOSignal[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const total = signals.length + ghosts.length
  return (
    <CollapsibleRowSection label={label} count={total}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Signal</th>
              <th className="whitespace-nowrap px-3 py-1.5">Address</th>
              <th className="px-3 py-1.5">Direction</th>
              <th className="px-3 py-1.5">PLC tag</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((signal) => {
              const mark = markFor(signal.id)
              return (
                <tr key={signal.id} className={sessionMarkClass('border-t', mark)}>
                  <td className="max-w-[14rem] truncate px-3 py-1.5">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Link
                        to={plantHref(signal.id, 'RobotIOSignal', signal)}
                        className="min-w-0 flex-1 truncate font-medium text-brand no-underline hover:underline"
                        title={signal.name}
                      >
                        {signal.name}
                      </Link>
                      <SessionMarkAffordances mark={mark} />
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-10 text-ink-3">
                    {signal.address ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">{signal.direction ?? '—'}</td>
                  <td className="px-3 py-1.5">
                    <PlcTagCell plcTags={signal.plcTags} plcTag={signal.plc_tag} />
                  </td>
                </tr>
              )
            })}
            {ghosts.map((ghost) => (
              <tr
                key={ghost.id}
                className={
                  ghost.view === 'dimmed'
                    ? 'border-t border-line bg-bg-sunk/70'
                    : 'border-t border-st-down/30 bg-st-down-bg'
                }
              >
                <td className="px-3 py-1.5" colSpan={4}>
                  <RobotGhostTile kind="RobotIOSignal" ghost={ghost} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function SignalSiteTable({
  label,
  sites
}: {
  label: string
  sites: PlantRobotRelationSite<PlantRobotIOSignal>[]
}): React.ReactElement {
  const groups = React.useMemo(() => groupRelationSites(sites), [sites])
  return (
    <CollapsibleRowSection label={label} count={groups.length}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Signal</th>
              <th className="whitespace-nowrap px-3 py-1.5">Address</th>
              <th className="px-3 py-1.5">Direction</th>
              <th className="px-3 py-1.5">Sites</th>
              <th className="px-3 py-1.5">PLC tag</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.node.id} className="border-t">
                <td className="max-w-[14rem] truncate px-3 py-1.5">
                  <Link
                    to={plantHref(group.node.id, 'RobotIOSignal', group.node)}
                    className="truncate font-medium text-brand no-underline hover:underline"
                    title={group.node.name}
                  >
                    {group.node.name}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 font-mono text-10 text-ink-3">
                  {group.node.address ?? '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">{group.node.direction ?? '—'}</td>
                <td className="whitespace-nowrap px-3 py-1.5 font-mono text-10 text-ink-3">
                  {formatGroupedSites(group.sites) ?? '—'}
                  {group.sites.length > 1 && (
                    <span className="ml-1 text-ink-4">({group.sites.length})</span>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <PlcTagCell plcTags={group.node.plcTags} plcTag={group.node.plc_tag} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function ConditionTable({
  conditions,
  ghosts = []
}: {
  conditions: PlantRobotCondition[]
  ghosts?: PlantSessionGhostView[]
}): React.ReactElement {
  const total = conditions.length + ghosts.length
  return (
    <CollapsibleRowSection label="Conditions" count={total}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Condition</th>
              <th className="px-3 py-1.5">Details</th>
            </tr>
          </thead>
          <tbody>
            {conditions.map((condition) => (
              <tr key={condition.id} className="border-t">
                <td className="px-3 py-1.5">
                  <Link
                    to={plantHref(condition.id, 'RobotCondition', condition)}
                    className="font-medium text-ink no-underline hover:text-brand hover:underline"
                  >
                    {condition.name}
                  </Link>
                </td>
                <td className="px-3 py-1.5 text-ink-3">{conditionSubtitle(condition) ?? '—'}</td>
              </tr>
            ))}
            {ghosts.map((ghost) => (
              <tr
                key={ghost.id}
                className={
                  ghost.view === 'dimmed'
                    ? 'border-t border-line bg-bg-sunk/70'
                    : 'border-t border-st-down/30 bg-st-down-bg'
                }
              >
                <td className="px-3 py-1.5" colSpan={2}>
                  <RobotGhostTile kind="RobotCondition" ghost={ghost} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function ConditionSiteTable({
  sites
}: {
  sites: PlantRobotRelationSite<PlantRobotCondition>[]
}): React.ReactElement {
  const groups = React.useMemo(() => groupRelationSites(sites), [sites])
  return (
    <CollapsibleRowSection label="Conditions" count={groups.length}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Condition</th>
              <th className="px-3 py-1.5">Details</th>
              <th className="px-3 py-1.5">Sites</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.node.id} className="border-t">
                <td className="px-3 py-1.5">
                  <Link
                    to={plantHref(group.node.id, 'RobotCondition', group.node)}
                    className="font-medium text-ink no-underline hover:text-brand hover:underline"
                  >
                    {group.node.name}
                  </Link>
                </td>
                <td className="px-3 py-1.5 text-ink-3">{conditionSubtitle(group.node) ?? '—'}</td>
                <td className="px-3 py-1.5 font-mono text-10 text-ink-3">
                  {formatGroupedSites(group.sites) ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function TopologySection({
  supervises,
  supervisedBy
}: {
  supervises: PlantRobotSupervisionRef[]
  supervisedBy: PlantRobotSupervisionRef[]
}): React.ReactElement | null {
  if (supervises.length === 0 && supervisedBy.length === 0) return null
  return (
    <section>
      <SectionHeader label="Topology" />
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 text-card-foreground shadow-sm">
        {supervisedBy.length > 0 && (
          <SupervisionChipRow label="Supervised by" rows={supervisedBy} />
        )}
        {supervises.length > 0 && <SupervisionChipRow label="Supervises" rows={supervises} />}
      </div>
    </section>
  )
}

function SupervisionChipRow({
  label,
  rows
}: {
  label: string
  rows: PlantRobotSupervisionRef[]
}): React.ReactElement {
  return (
    <div>
      <div className="mb-1.5 text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
        {label}
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.map((row) => (
          <Link
            key={`${label}:${row.controller.id}:${row.role ?? ''}`}
            to={plantHref(row.controller.id, 'RobotController', row.controller)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-12 font-medium no-underline hover:opacity-80 ${supervisionRoleClass(row.role)}`}
            title={row.networkPath ? `network: ${row.networkPath}` : undefined}
          >
            <span>{row.controller.display_name ?? row.controller.name}</span>
            {row.role && (
              <Badge variant="outline" className="text-10">
                {row.role.replace(/_/g, ' ')}
              </Badge>
            )}
            {row.networkPath && (
              <span className="font-mono text-10 opacity-80">{row.networkPath}</span>
            )}
          </Link>
        ))}
      </div>
    </div>
  )
}

function supervisionRoleClass(role: string | undefined): string {
  switch (role) {
    case 'master':
      return 'border-emerald-400/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
    case 'slave':
      return 'border-sky-400/40 bg-sky-500/10 text-sky-800 dark:text-sky-200'
    case 'line_tracking':
      return 'border-violet-400/40 bg-violet-500/10 text-violet-800 dark:text-violet-200'
    default:
      return 'border-border bg-bg-sunk text-ink-2'
  }
}

function JobSiteLinksSection({
  calls,
  calledBy,
  callsLabel = 'Calls',
  calledByLabel = 'Called by'
}: {
  calls: PlantRobotRelationSite<PlantRobotJob>[]
  calledBy: PlantRobotRelationSite<PlantRobotJob>[]
  callsLabel?: string
  calledByLabel?: string
}): React.ReactElement {
  return (
    <section className="grid gap-3 md:grid-cols-2">
      <JobSiteLinkList label={callsLabel} sites={calls} />
      {calledBy.length > 0 && <JobSiteLinkList label={calledByLabel} sites={calledBy} />}
    </section>
  )
}

function JobSiteLinkList({
  label,
  sites
}: {
  label: string
  sites: PlantRobotRelationSite<PlantRobotJob>[]
}): React.ReactElement {
  const groups = React.useMemo(() => groupRelationSites(sites), [sites])
  if (sites.length === 0) {
    return (
      <div>
        <SectionHeader label={label} subtitle="0" />
        <span className="text-sm text-ink-3">None</span>
      </div>
    )
  }
  return (
    <CollapsibleRowSection label={label} count={groups.length}>
      <div className="overflow-x-auto rounded-lg border bg-card text-card-foreground shadow-sm">
        <table className="w-full border-collapse text-12">
          <thead>
            <tr className="text-left text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
              <th className="px-3 py-1.5">Job</th>
              <th className="px-3 py-1.5">Sites</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.node.id} className="border-t">
                <td className="px-3 py-1.5">
                  <Link
                    to={plantHref(group.node.id, 'RobotJob', group.node)}
                    className="font-medium text-ink no-underline hover:text-brand hover:underline"
                  >
                    {group.node.name}
                  </Link>
                </td>
                <td className="px-3 py-1.5 font-mono text-10 text-ink-3">
                  {formatGroupedSites(group.sites) ?? '—'}
                  {group.sites.length > 1 && (
                    <span className="ml-1 text-ink-4">({group.sites.length})</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleRowSection>
  )
}

function formatSiteSubtitle(site: {
  line?: number
  opcode?: string
  instruction?: string
}): string | null {
  const bits: string[] = []
  if (typeof site.line === 'number') bits.push(`line ${site.line}`)
  if (site.opcode) bits.push(site.opcode)
  else if (site.instruction) bits.push(site.instruction)
  return bits.length > 0 ? bits.join(' · ') : null
}

function RobotScadaMappingsSection({ tags }: { tags: PlantPlcScadaTag[] }): React.ReactElement {
  return (
    <section>
      <SectionHeader label="Maps to SCADA" subtitle={`${tags.length}`} />
      <div className="flex flex-wrap gap-2">
        {tags.map((entry) => {
          const props = entry.relationship?.props ?? {}
          const method =
            typeof props['mapping_method'] === 'string'
              ? (props['mapping_method'] as string)
              : typeof props['method'] === 'string'
                ? (props['method'] as string)
                : undefined
          const needsReview = props['needs_review'] === true
          const confidence = entry.relationship?.confidence
          const href = plantHref(entry.id, 'Tag', entry)
          return (
            <Link
              key={`${entry.edge}:${entry.id}`}
              to={href}
              className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border border-brand/40 bg-brand-soft px-2.5 py-1 text-12 font-medium text-brand no-underline hover:bg-brand-soft/70"
              title={[
                entry.name,
                entry.edge,
                method ? `method: ${method}` : null,
                confidence !== undefined ? `confidence: ${Math.round(confidence * 100)}%` : null,
                needsReview ? 'needs review' : null
              ]
                .filter(Boolean)
                .join(' · ')}
            >
              <span className="shrink-0 font-mono text-10 uppercase tracking-[0.04em] text-brand/80">
                maps
              </span>
              <span className="min-w-0 max-w-[14rem] truncate">{entry.name}</span>
              {method && <span className="shrink-0 font-mono text-10 text-brand/70">{method}</span>}
              {needsReview && (
                <span
                  aria-label="Needs review"
                  title="Needs review"
                  className="shrink-0 rounded border border-amber-500/50 bg-amber-500/10 px-1 py-px text-9 font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300"
                >
                  Review
                </span>
              )}
            </Link>
          )
        })}
      </div>
    </section>
  )
}

function RobotPlcMappingsSection({
  mappings
}: {
  mappings: PlantRobotPlcTagMapping[]
}): React.ReactElement {
  return (
    <section>
      <SectionHeader label="PLC mappings" subtitle={`${mappings.length}`} />
      <div className="flex flex-wrap gap-2">
        {mappings.map((mapping) => (
          <Link
            key={mapping.tag.id}
            to={plantHref(mapping.tag.id, 'PLCTag', mapping.tag)}
            className="inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border border-violet-400/40 bg-violet-500/10 px-2.5 py-1 text-12 font-medium text-violet-800 no-underline hover:bg-violet-500/20 dark:text-violet-200"
            title={[
              mapping.tag.name,
              mapping.method ? `method: ${mapping.method}` : null,
              mapping.register ? `register: ${mapping.register}` : null,
              mapping.direction ? `direction: ${mapping.direction}` : null,
              mapping.confidence !== undefined
                ? `confidence: ${Math.round(mapping.confidence * 100)}%`
                : null
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <span className="min-w-0 max-w-[14rem] truncate">{mapping.tag.name}</span>
            {mapping.method && (
              <span className="shrink-0 font-mono text-10 text-violet-700/80 dark:text-violet-200/80">
                {mapping.method}
              </span>
            )}
            {mapping.register && (
              <span className="shrink-0 font-mono text-10 text-violet-700/80 dark:text-violet-200/80">
                {mapping.register}
              </span>
            )}
          </Link>
        ))}
      </div>
    </section>
  )
}

function SourceCodeSection({ code }: { code: string }): React.ReactElement {
  return (
    <section>
      <SectionHeader label="Source code" />
      <pre className="max-h-130 overflow-auto rounded-xl border bg-bg-sunk p-4 text-xs leading-relaxed text-ink">
        {code}
      </pre>
    </section>
  )
}

function controllerSubtitle(controller: PlantRobotController): string | undefined {
  return [controller.vendor, controller.model, controller.controller_series, controller.ip_address]
    .filter(Boolean)
    .join(' · ')
}

function armSubtitle(arm: PlantRobotArm): string | undefined {
  return [arm.group, arm.model, arm.payload_kg ? `${arm.payload_kg} kg` : undefined]
    .filter(Boolean)
    .join(' · ')
}

function jobSubtitle(job: PlantRobotJob): string | undefined {
  return [
    job.job_type,
    job.group,
    job.is_master ? 'master' : undefined,
    job.instruction_count !== undefined ? `${job.instruction_count} instructions` : undefined
  ]
    .filter(Boolean)
    .join(' · ')
}

function signalSubtitle(signal: PlantRobotIOSignal): string | undefined {
  return [signal.address, signal.direction, signal.plc_tag].filter(Boolean).join(' · ')
}

function humanize(value: string | undefined): string | undefined {
  return value ? value.replace(/_/g, ' ') : undefined
}

function formatBytes(bytes: number | undefined): string | undefined {
  if (bytes === undefined) return undefined
  return bytes >= 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${bytes} B`
}

function visionProcessSubtitle(process: PlantVisionProcess): string | undefined {
  return [
    process.vision_system,
    process.type_label,
    process.applications?.map(humanize).join(', '),
    process.source_file,
    formatBytes(process.vision_data_size_bytes)
  ]
    .filter(Boolean)
    .join(' · ')
}

function visionDataSubtitle(data: PlantVisionData): string | undefined {
  return [
    humanize(data.vision_data_type),
    data.type_label,
    data.operations?.join(', '),
    data.source_file,
    formatBytes(data.vision_data_size_bytes)
  ]
    .filter(Boolean)
    .join(' · ')
}

function conditionSubtitle(condition: PlantRobotCondition): string | undefined {
  return [
    condition.scheme,
    condition.index !== undefined ? `#${condition.index}` : undefined,
    condition.condition_type
  ]
    .filter(Boolean)
    .join(' · ')
}
